import {Identifier} from "../common/Identifier";
import {BasicBillableItem,  WorkHoursBillableByTask} from "./AccountBook";

/**
 * Represents an item associated with the time tracked for a specific project task within an outgoing invoice.
 *
 * This interface is used to encapsulate the time-related data for each task that is billable within the context of an invoice.
 * It associates a unique identifier with the billable work hours of a specific task to ensure accurate tracking and invoicing.
 *
 * @extends WorkHoursBillableByTask Inherits properties and behavior for handling billable work hours by task.
 * @property id A unique identifier for the task time item, represented as a number.
 */
export interface OutgoingInvoiceProjectTaskTimeItem extends WorkHoursBillableByTask {
    id: Identifier< number >
}

/**
 * Determines whether a given object represents an Invoice Project Task Time item.
 *
 * This function is used to validate objects, ensuring they conform to the structure
 * expected for processing items tied to a project's task-specific billable time.
 * This is typically used to handle outgoing invoice data related to project tasks.
 *
 * The method checks the presence of specific properties (`id`, `timeItems`, and `phaseTaskId`)
 * that are required for the object to qualify as an OutgoingInvoiceProjectTaskTimeItem.
 *
 * @param obj - The object to be evaluated. It is expected to be a generic billable item.
 * @return A boolean value indicating whether the object is an OutgoingInvoiceProjectTaskTimeItem.
 *         Returns `true` if the object contains all the required properties,
 *         otherwise returns `false`.
 */
export function isInvoiceProjectTaskTimeItem( obj: BasicBillableItem ): obj is OutgoingInvoiceProjectTaskTimeItem {
    return obj.hasOwnProperty("id") && obj.hasOwnProperty("timeItems") && obj.hasOwnProperty("phaseTaskId");
}